<template>
  <a-modal
    :visible="visible"
    :footer="null"
    :width="480"
    :closable="true"
    :maskClosable="false"
    wrapClassName="assign-caregiver-modal"
    @cancel="$emit('cancel')"
  >
    <template slot="title">
      <div class="modal-title-wrap">
        <span class="modal-title">指派护工</span>
        <span v-if="order" class="modal-order-no">{{ order.orderNo }}</span>
      </div>
    </template>

    <div v-if="order" class="assign-modal-body">
      <div class="order-info-box">
        <p class="order-hospital">{{ order.hospital }}</p>
        <p class="order-dept">
          {{ order.department }} · {{ order.bedNo }}
        </p>
        <p v-if="order.preferredCaregiverName" class="order-preference">
          用户意向护工：<strong>{{ order.preferredCaregiverName }}</strong>（可确认或改派其他护工）
        </p>
        <p v-else-if="order.assignMode === 'random'" class="order-preference random">
          用户选择随机分配，请根据排班安排合适护工
        </p>
      </div>

      <div class="caregiver-list">
        <label
          v-for="cg in caregivers"
          :key="cg.id"
          class="caregiver-item"
          :class="{ selected: selectedId === cg.id, disabled: cg.status === 'busy' }"
        >
          <input
            type="radio"
            name="caregiver"
            :value="cg.id"
            :disabled="cg.status === 'busy'"
            v-model="selectedId"
            class="caregiver-radio"
          />
          <div class="caregiver-avatar" :style="{ background: cg.avatarBg }">
            {{ cg.name.charAt(0) }}
          </div>
          <div class="caregiver-info">
            <div class="caregiver-name-row">
              <span class="caregiver-name">{{ cg.name }}</span>
              <span class="level-tag" :class="'level-' + cg.levelColor">{{ cg.level }}</span>
            </div>
            <p class="caregiver-meta">
              {{ cg.hours }} 小时
              <span class="meta-dot">·</span>
              <span :class="cg.status === 'idle' ? 'status-idle' : 'status-busy'">
                {{ cg.status === "idle" ? "空闲" : "有任务" }}
              </span>
            </p>
          </div>
        </label>
      </div>

      <div class="modal-footer">
        <a-button class="btn-cancel" @click="$emit('cancel')">取消</a-button>
        <a-button
          type="primary"
          class="btn-confirm"
          :disabled="!selectedId"
          :loading="loading"
          @click="handleConfirm"
        >
          确认指派
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { dispatchApi } from "./dispatchApi";

export default {
  name: "AssignCaregiverModal",
  props: {
    visible: { type: Boolean, default: false },
    order: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedId: null,
      caregivers: [],
      caregiverLoading: false,
    };
  },
  watch: {
    visible(val) {
      if (val) {
        this.selectedId = this.order && this.order.preferredCaregiverId
          ? this.order.preferredCaregiverId
          : null;
        this.loadCaregivers();
      }
    },
  },
  methods: {
    async loadCaregivers() {
      this.caregiverLoading = true;
      try {
        this.caregivers = await dispatchApi.listCaregivers();
      } catch (e) {
        this.$message.error(e.message || "护工列表加载失败");
        this.caregivers = [];
      } finally {
        this.caregiverLoading = false;
      }
    },
    handleConfirm() {
      if (!this.selectedId || !this.order) return;
      this.$emit("confirm", { orderId: this.order.id, caregiverId: this.selectedId });
    },
  },
};
</script>

<style scoped>
.modal-title-wrap {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #0f172a;
}

.modal-order-no {
  font-size: 0.8125rem;
  color: #94a3b8;
  font-family: monospace;
}

.order-info-box {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.75rem;
  padding: 0.875rem 1rem;
  margin-bottom: 1.25rem;
}

.order-hospital {
  font-weight: 600;
  color: #991b1b;
  margin: 0 0 0.25rem;
  font-size: 0.9375rem;
}

.order-dept {
  margin: 0;
  font-size: 0.8125rem;
  color: #b91c1c;
}

.order-preference {
  margin: 0.625rem 0 0;
  padding-top: 0.5rem;
  border-top: 1px dashed #fecaca;
  font-size: 0.8125rem;
  color: #9a3412;
  line-height: 1.5;
}

.order-preference.random {
  color: #0369a1;
}

.caregiver-list {
  max-height: 320px;
  overflow-y: auto;
  margin-bottom: 1.25rem;
}

.caregiver-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  margin-bottom: 0.625rem;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.caregiver-item:hover:not(.disabled) {
  border-color: #93c5fd;
  background: #f8fafc;
}

.caregiver-item.selected {
  border-color: #0082d4;
  background: #eff6ff;
}

.caregiver-item.disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.caregiver-radio {
  flex-shrink: 0;
}

.caregiver-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: #fff;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.caregiver-info {
  flex: 1;
  min-width: 0;
}

.caregiver-name-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.caregiver-name {
  font-weight: 600;
  color: #0f172a;
}

.level-tag {
  font-size: 0.6875rem;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  font-weight: 600;
}

.level-gold {
  background: #fef3c7;
  color: #b45309;
}

.level-pro {
  background: #dbeafe;
  color: #1d4ed8;
}

.level-silver {
  background: #f1f5f9;
  color: #64748b;
}

.caregiver-meta {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  color: #64748b;
}

.meta-dot {
  margin: 0 0.25rem;
}

.status-idle {
  color: #16a34a;
}

.status-busy {
  color: #ea580c;
}

.modal-footer {
  display: flex;
  gap: 0.75rem;
}

.btn-cancel,
.btn-confirm {
  flex: 1;
  height: 44px !important;
  border-radius: 0.75rem !important;
  font-weight: 600 !important;
}

.btn-confirm {
  background: #0082d4 !important;
  border-color: #0082d4 !important;
}
</style>
